import React from "react";
import { SearchableDropdown } from "../../../ui/SearchableDropdown";
import { FilterButton } from "./FilterButton";
import { METADATA_VALUE_LIMIT } from "../constants";
import type { MetadataValue, TagFilter } from "../types";
import { TagValue } from "../../tag-aliases/TagValue";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import { TagIcon } from "@heroicons/react/20/solid";

interface SearchableTagDropdownProps {
  tagKey: string;
  filter: TagFilter | undefined;
  isExpanded: boolean;
  values: MetadataValue[];
  selectedValues: Set<string>;
  isLoading: boolean;
  onToggle: () => void;
  onSelectValue: (value: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onRemove?: () => void;
  canApply: boolean;
}

/**
 * The value picker for one tag key: a searchable multi-select showing how many
 * traces sit behind each value, biggest first.
 *
 * The order is the API's — most-used first (design doc 035) — rendered
 * verbatim. Re-sorting here would put this component and every other caller of
 * /metadata/values in disagreement about which value comes first.
 *
 * Sibling of SearchableCriteriaDropdown — both are thin, domain-labelled
 * wrappers around the generic ui/SearchableDropdown, so tags and criteria share
 * one search box, one positioning rule and one keyboard behaviour. Before
 * #1415 the tag picker was a bare checkbox list, which turned into
 * scroll-and-guess as soon as a customer had more than a handful of orgs.
 *
 * Values render under their workspace display name (#1410) and are searchable
 * by it: someone who named a UUID "Deckers" types "Deckers", not the UUID they
 * can no longer see. The raw value stays the identity that is selected, sent
 * and counted — only the text changes.
 */
export function SearchableTagDropdown({
  tagKey,
  filter,
  isExpanded,
  values,
  selectedValues,
  isLoading,
  onToggle,
  onSelectValue,
  onApply,
  onCancel,
  onRemove,
  canApply,
}: SearchableTagDropdownProps) {
  const { aliases } = useTagAliases();
  const counts = React.useMemo(
    () => new Map(values.map((v) => [v.value, v.count])),
    [values],
  );
  const names = React.useMemo(() => values.map((v) => v.value), [values]);

  // Search what the user can see as well as what is underneath it: a named
  // value shows its name, so typing the name has to find it, and the raw value
  // still matches for anyone working from a UUID they pasted.
  const matchesSearch = React.useCallback(
    (searchTerm: string, value: string) => {
      const needle = searchTerm.toLowerCase().trim();
      if (!needle) return true;
      return (
        value.toLowerCase().includes(needle) ||
        formatTagValue(value, aliases).toLowerCase().includes(needle)
      );
    },
    [aliases],
  );

  // The server keeps the most-frequent values and cuts the rest, so a full
  // window means the list on screen may not be the whole list. Say so rather
  // than let the search box imply it searched everything (#1414's lesson).
  const isPartial = values.length >= METADATA_VALUE_LIMIT;

  return (
    <SearchableDropdown
      isExpanded={isExpanded}
      onToggle={onToggle}
      values={names}
      selectedValues={selectedValues}
      onSelectValue={onSelectValue}
      onApply={onApply}
      onCancel={onCancel}
      isLoading={isLoading}
      searchPlaceholder={`Search ${tagKey}…`}
      emptyMessage="No values available"
      noResultsMessage={(searchTerm) => `No values match "${searchTerm}"`}
      canApply={canApply}
      // Wider than the 350px default: a tag value is often a 36-character
      // identifier, and it now shares its row with a rename control and a trace
      // count. At the default width every unnamed value wraps onto two lines.
      minWidth="460px"
      maxWidth="560px"
      searchMatchFn={matchesSearch}
      // The checkbox still carries the raw value — the alias is text. A label's
      // activation behaviour ignores clicks on interactive descendants, so the
      // rename control inside it doesn't toggle the box.
      renderOptionLabel={(value) => <TagValue value={value} />}
      renderOptionMeta={(value) => {
        const count = counts.get(value);
        if (count === undefined) return null;
        return (
          <span title={`${count.toLocaleString()} traces`}>
            {count.toLocaleString()}
          </span>
        );
      }}
      footerNote={
        isPartial
          ? `Showing the ${METADATA_VALUE_LIMIT.toLocaleString()} highest-volume values. Narrow with another filter to see more.`
          : undefined
      }
      renderTrigger={({ isExpanded, onClick }) => (
        <FilterButton
          label={tagKey}
          isActive={!!filter}
          isExpanded={isExpanded}
          onClick={onClick}
          onRemove={onRemove}
          icon={<TagIcon className="h-4 w-4" />}
          badge={filter ? filter.values.length : undefined}
        />
      )}
      renderSelectedCount={(count) => `${count} selected`}
    />
  );
}
